import { http } from './client'
import type { AuditEntry, MfaState, Page, Role, StaffMe, StaffUser } from './types'

/** Thin wrappers over /api/v1/admin (SPEC-002 §7–§9). */
export const adminApi = {
  // Sign-in
  login: (email: string, password: string) =>
    http
      .post<{ data: { mfa: MfaState } }>('/admin/auth/login', { email, password })
      .then((r) => r.data.data.mfa),
  verifyMfa: (payload: { code: string } | { backup_code: string }) =>
    http.post<{ data: StaffMe }>('/admin/auth/mfa/verify', payload).then((r) => r.data.data),
  startEnrollment: () =>
    http
      .post<{ data: { secret: string; otpauth_url: string } }>('/admin/auth/mfa/enroll')
      .then((r) => r.data.data),
  confirmEnrollment: (code: string) =>
    http
      .post<{ data: { backup_codes: string[]; user: StaffMe } }>('/admin/auth/mfa/enroll/confirm', { code })
      .then((r) => r.data.data),
  confirmIdentity: (password: string, code: string) => http.post('/admin/auth/confirm', { password, code }),
  me: () => http.get<{ data: StaffMe }>('/admin/me').then((r) => r.data.data),
  logout: () => http.post('/admin/auth/logout'),
  forgotPassword: (email: string) => http.post('/admin/auth/forgot-password', { email }),
  setPassword: (payload: {
    purpose: 'invite' | 'reset'
    email: string
    token: string
    password: string
    password_confirmation: string
  }) => http.post('/admin/auth/set-password', payload),

  // Staff
  listStaff: (params: Record<string, string | number>) =>
    http.get<Page<StaffUser>>('/admin/users', { params }).then((r) => r.data),
  inviteStaff: (payload: { name: string; email: string; roles: string[]; reason?: string }) =>
    http.post<{ data: StaffUser }>('/admin/users', payload).then((r) => r.data.data),
  changeRoles: (id: string, roles: string[], reason: string) =>
    http.patch<{ data: StaffUser }>(`/admin/users/${id}/roles`, { roles, reason }).then((r) => r.data.data),
  suspend: (id: string, reason: string) =>
    http.post<{ data: StaffUser }>(`/admin/users/${id}/suspend`, { reason }).then((r) => r.data.data),
  reactivate: (id: string, reason: string) =>
    http.post<{ data: StaffUser }>(`/admin/users/${id}/reactivate`, { reason }).then((r) => r.data.data),
  resetMfa: (id: string, reason: string) =>
    http.post<{ data: StaffUser }>(`/admin/users/${id}/mfa/reset`, { reason }).then((r) => r.data.data),

  // Roles
  listRoles: () =>
    http.get<{ data: Role[]; meta: { permissions: string[] } }>('/admin/roles').then((r) => r.data),
  updateRolePermissions: (role: string, permissions: string[], reason: string) =>
    http
      .put<{ data: Role }>(`/admin/roles/${role}/permissions`, { permissions, reason })
      .then((r) => r.data.data),

  // Audit log
  listAudit: (params: Record<string, string | number>) =>
    http.get<Page<AuditEntry>>('/admin/audit-logs', { params }).then((r) => r.data),
}
